import moment from "moment";
import PatientListEntry from "../../types/PatientListEntry";

interface PrintOptions {
  departmentName?: string;
  rowsPerPage?: number;
}

// function to generate HTML to use for printing the patient list
export const printPatientList = (
  patients: PatientListEntry[],
  options: PrintOptions = {}
): void => {
  const { departmentName, rowsPerPage = 10 } = options;

  const printDate = moment().format("M/D/YYYY HH:mm:ss");
  const totalPatients = patients.length;
  const totalPages = Math.ceil(totalPatients / rowsPerPage);

  // Split patients into pages
  const pages: PatientListEntry[][] = [];
  for (let i = 0; i < patients.length; i += rowsPerPage) {
    pages.push(patients.slice(i, i + rowsPerPage));
  }

  // Generate table header
  const tableHeader = `
    <thead>
      <tr>
        <th>Location</th>
        <th>MRN</th>
        <th>Patient</th>
        <th>LOS</th>
        <th>Care Team</th>
        <th>EDD</th>
        <th>Disposition</th>
        <th>Milestones</th>
        <th>Notes</th>
      </tr>
    </thead>
  `;

  // Generate rows for a page
  const generateRows = (pagePatients: PatientListEntry[]) => {
    return pagePatients
      .map((row) => {
        const milestones = row.milestones
          .filter(
            (m) =>
              m.milestone_status === "INCOMPLETE" &&
              !m.milestone.is_clinical_marker
          )
          .map((m) => m.milestone.display_name)
          .join(", ");

        const notes = row.notes.length > 0 ? row.notes[0].content : "";

        return `
          <tr>
            <td>${row.current_bed || ""}</td>
            <td>${row.mrn || ""}</td>
            <td>${row.first_name || ""} ${row.last_name || ""}</td>
            <td>${row.los || ""}</td>
            <td>${row.attending_provider || ""}</td>
            <td>${
              row.current_edd
                ? moment(row.current_edd.edd_value).format("MM/DD/YY A")
                : ""
            }</td>
            <td>${row.current_dispositions
              .map((d) => d.disposition.display_name)
              .join(", ")}</td>
            <td>${milestones}</td>
            <td>${notes}</td>
          </tr>
        `;
      })
      .join("");
  };

  // Generate page content
  const generatePage = (
    pagePatients: PatientListEntry[],
    pageNumber: number,
    startRow: number,
    endRow: number
  ) => {
    return `
      <div class="page">
        <table>
          ${tableHeader}
          <tbody>
            ${generateRows(pagePatients)}
          </tbody>
        </table>
        
        <div class="page-footer">
          <span>${printDate}</span>
          <span>Row ${startRow}-${endRow}  pg. ${pageNumber} of ${totalPages}</span>
        </div>
      </div>
    `;
  };

  const generateFilename = () => {
    const date = moment().format("YYYY-MM-DD_HH-mm-ss");
    const deptName = departmentName.replace(/[^a-zA-Z0-9]/g, "_");
    return `Patient_List_${deptName}_${date}`;
  };

  document.title = generateFilename();

  // Generate all pages
  const pagesHTML = pages
    .map((pagePatients, index) => {
      const pageNumber = index + 1;
      const startRow = index * rowsPerPage + 1;
      const endRow = Math.min(
        startRow + pagePatients.length - 1,
        totalPatients
      );
      return generatePage(pagePatients, pageNumber, startRow, endRow);
    })
    .join("");

  // Complete HTML document
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
            @page {
                size: landscape;
                margin: 0.5in;
            }
            
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }
            
            body {
                font-family: "Roboto", "Helvetica", "Arial", sans-serif;
                font-size: 11pt;
                color: #000;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            
            .page {
                page-break-after: always;
                page-break-inside: avoid;
            }
            
            .page:last-child {
                page-break-after: auto;
            }
            
            table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
            }
            
            thead {
                background-color: #eeeeee;
            }
            
            th {
                text-align: left;
                padding: 10px 12px;
                font-weight: 700;
                font-size: 10pt;
                background-color: white;
                border: none;
                border-bottom: 1px solid #e0e0e0;
                white-space: nowrap;
            }
            
            td {
                padding: 10px 12px;
                border: none;
                border-bottom: 1px solid #e0e0e0;
                vertical-align: top;
                font-size: 10pt;
                line-height: 1.4;
                word-wrap: break-word;
            }
            
            /* Column widths */
            th:nth-child(1), td:nth-child(1) { width: 8%; }
            th:nth-child(2), td:nth-child(2) { width: 10%; }
            th:nth-child(3), td:nth-child(3) { width: 15%; }
            th:nth-child(4), td:nth-child(4) { width: 5%; }
            th:nth-child(5), td:nth-child(5) { width: 12%; }
            th:nth-child(6), td:nth-child(6) { width: 10%; }
            th:nth-child(7), td:nth-child(7) { width: 15%; }
            th:nth-child(8), td:nth-child(8) { width: 15%; }
            th:nth-child(9), td:nth-child(9) { width: 10%; }
            
            tbody tr {
                background-color: #ffffff;
            }

            tbody tr:last-child td {
              border-bottom: none;
            }
            
            .page-footer {
                padding-top: 12px;
                border-top: 1px solid #ccc;
                display: flex;
                justify-content: space-between;
                font-size: 10pt;
                color: #333;
            }
            
            @media print {
                body {
                margin: 0;
                padding: 0;
                }
                
                .page {
                page-break-after: always;
                }
                
                .page:last-child {
                page-break-after: auto;
                }
            }
        </style>
      </head>
      <body>
        ${pagesHTML}
      </body>
    </html>
  `;

  // Create a hidden iframe for printing
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.srcdoc = html;

  document.body.appendChild(iframe);

  // Wait for iframe to load
  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();

      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 100);
  };
};
